import {
	useApiGetBottomUpCatalogTreeIntegrations,
	useApiGetCatalogTreeIntegrations,
} from '@repo/api-codegen';
import { integrationList } from '@repo/common/constants/integration/integrations';
import { EntityType } from '@repo/common/enums/entityType';
import { useNavigateHandler } from '@repo/common/hooks/useNavigate';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useEntityBreadcrumbTeam } from '../../../hooks/useEntityBreadcrumbs';
import { buildResourceUrl } from '../../../utils/navigationUtils';
import { BottomUpTreeIntegrationNode } from '../components/BottomUpTreeIntegrationNode';
import { DataTreeLoader } from '../components/DataTreeLoader';
import { EntityTreeNode } from '../components/EntityTreeNode';
import type { CommonEntityTreeNode } from '../utils';
import {
	catalogFilterFromTreeNode,
	catalogFilterQueryParamsFromCatalogTreeNode,
	getIcon,
	getLabel,
	isIntegrationRootNode,
} from './utils';

interface CatalogTreeProps {
	teamId: string;
	open: boolean;
}

export function CatalogTreeV2({ teamId, open }: CatalogTreeProps) {
	// Queries
	const { data: integrationsData, isLoading: isLoadingIntegrations } =
		useApiGetCatalogTreeIntegrations(
			{
				queryParams: {
					team_id: teamId,
				},
			},
			{
				enabled: open,
			}
		);

	const {
		data: bottomUpIntegrations,
		isLoading: isLoadingBottomUpIntegrations,
	} = useApiGetBottomUpCatalogTreeIntegrations(
		{
			queryParams: {
				team_id: teamId,
			},
		},
		{
			enabled: open,
		}
	);

	// Hooks
	const { setTeamBreadcrumb } = useEntityBreadcrumbTeam();
	const navigateHandler = useNavigateHandler();

	// Callbacks
	const handleNavigateCatalogNode = useCallback(
		(event: React.MouseEvent, node: CommonEntityTreeNode) => {
			const catalogFilterQueryParams =
				catalogFilterQueryParamsFromCatalogTreeNode(node);

			const catalogPageURL = `/teams/${teamId}/catalog?filters=${encodeURIComponent(
				JSON.stringify(catalogFilterQueryParams)
			)}`;

			if (node.entity.entity_type !== EntityType.integration) {
				const resourceURL = buildResourceUrl(node.entity);
				if (resourceURL !== '#') {
					navigateHandler(event)(resourceURL);
					return;
				}
			}

			navigateHandler(event)(catalogPageURL);
		},
		[teamId, navigateHandler]
	);

	const handleNavigate = useCallback(
		(event: React.MouseEvent, node: CommonEntityTreeNode) => {
			setTeamBreadcrumb(teamId);
			handleNavigateCatalogNode(event, node);
		},
		[setTeamBreadcrumb, teamId, handleNavigateCatalogNode]
	);

	// Data
	const nodes: React.ReactNode[] = useMemo(() => {
		// Builds the top down tree nodes
		const topDownNodes: { sort: string; node: React.ReactNode }[] =
			integrationsData
				?.filter((integration) => isIntegrationRootNode(integration))
				.filter((integration) => {
					const integrationSpec = integrationList.find(
						(spec) => spec.type === integration.type
					);
					return !integrationSpec?.isExtension;
				})
				.map((integration) => ({
					sort: integration.name,
					node: (
						<EntityTreeNode
							key={integration.id}
							node={{
								id: integration.id,
								hasChildren: true,
								integration,
								entity: {
									id: integration.id,
									entity_type: EntityType.integration,
									icon: '',
									title: integration.name,
								},
							}}
							getIcon={getIcon}
							level={0}
							onClick={handleNavigate}
							getLabel={getLabel}
							childFilterOperands={catalogFilterFromTreeNode}
						/>
					),
				})) || [];

		// Builds the bottom up tree nodes
		const bottomUpNodes: { sort: string; node: React.ReactNode }[] = (
			bottomUpIntegrations ?? []
		)
			.filter((integration) =>
				isIntegrationRootNode({
					id: integration.integration_id,
					type: integration.type,
					name: integration.name,
				})
			)
			.filter((integration) => {
				const integrationSpec = integrationList.find(
					(spec) => spec.type === integration.type
				);
				return !integrationSpec?.isExtension;
			})
			.map((tree) => ({
				sort: tree.name,
				node: (
					<BottomUpTreeIntegrationNode
						key={tree.integration_id}
						name={tree.name}
						teamId={teamId}
						id={tree.integration_id}
						integration={{
							id: tree.integration_id,
							name: tree.name,
							type: tree.type,
						}}
						onClick={handleNavigate}
					/>
				),
			}));

		// Combines the top down and bottom up tree nodes and sorts them by title
		return [...topDownNodes, ...bottomUpNodes]
			.sort((a, b) => a.sort.localeCompare(b.sort))
			.map((node) => node.node);
	}, [integrationsData, bottomUpIntegrations, handleNavigate, teamId]);

	if (isLoadingIntegrations || isLoadingBottomUpIntegrations) {
		return <DataTreeLoader />;
	}

	return <>{nodes}</>;
}
